import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Dummy data for the chart
const data = [
  { name: 'Jan', volume1: 400, volume2: 300 },
  { name: 'Feb', volume1: 300, volume2: 250 },
  { name: 'Mar', volume1: 900, volume2: 850 },
  { name: 'Apr', volume1: 450, volume2: 500 },
  { name: 'May', volume1: 600, volume2: 550 },
  { name: 'Jun', volume1: 700, volume2: 650 },
  { name: 'Jul', volume1: 500, volume2: 600 },
  { name: 'Aug', volume1: 900, volume2: 850 },
  { name: 'Sep', volume1: 200, volume2: 250 },
  { name: 'Oct', volume1: 1100, volume2: 1050 },
  { name: 'Nov', volume1: 1200, volume2: 1150 },
  { name: 'Dec', volume1: 1300, volume2: 1250 },
];

const initialDatasetsConfig = [
  // Ancillary Services datasets
  { id: 'rc_awd', label: 'RC AWD', color: '#8884d8' },
  { id: 'a_s_spinning', label: 'A/S Spinning', color: '#ff7300' },
  { id: 'a_s_reg_up', label: 'A/S Regulation Up', color: '#82ca9d' },
  { id: 'a_s_non_spin', label: 'A/S Non-Spin', color: '#ff6b6b' },
  { id: 'a_s_reg_down_mileage', label: 'A/S Reg Down Mileage', color: '#8884d8' },
  { id: 'a_s_reg_up_mileage', label: 'A/S Reg Up Mileage', color: '#ff7300' },
  { id: 'a_s_reg_down', label: 'A/S Regulation Down', color: '#82ca9d' },
  { id: 'ramp_up', label: 'Ramp Up', color: '#ff6b6b' },
  { id: 'ramp_down', label: 'Ramp Down', color: '#8884d8' },
  { id: 'flexible_ramp_up', label: 'Flexible Ramp Up', color: '#ff7300' },
  { id: 'flexible_ramp_down', label: 'Flexible Ramp Down', color: '#82ca9d' },

  // Energy datasets
  { id: 'energy', label: 'Energy', color: '#8884d8' },
  { id: 'export', label: 'Export', color: '#ff7300' },
  { id: 'meter', label: 'Meter', color: '#82ca9d' },
  { id: 'ard', label: 'ARD', color: '#ff6b6b' },
  { id: 'generation', label: 'Generation', color: '#8884d8' },
  { id: 'generation_award', label: 'Generation Award', color: '#ff7300' },
  { id: 'energy_bid', label: 'Energy Bid', color: '#82ca9d' },
  { id: 'energy_only_offer', label: 'Energy Only Offer', color: '#ff6b6b' },
  { id: 'reliability_deployment_order', label: 'Reliability Deployment Order', color: '#8884d8' },
];

const StepChart: React.FC = () => {
  // State to manage visibility of each line
  const [visibility, setVisibility] = useState<any>(
    initialDatasetsConfig.reduce((acc: any, dataset) => {
      acc[dataset.id] = true; // Default to showing all datasets
      return acc;
    }, {})
  );

  const toggleVisibility = (id: string) => {
    setVisibility((prev: any) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div
      className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-lg"
      style={{
        fontFamily: 'Poppins, sans-serif', // Apply Poppins font
      }}
    >
      {/* Heading with clickable labels */}
      <div
        className="text-gray-700 text-sm sm:text-base font-medium mb-4 flex items-center cursor-pointer"
      >
        <span className="mr-3">Product:</span>

        <span
          onClick={() => toggleVisibility('a_s_reg_down')}
          className={`mr-3 ${visibility['a_s_reg_down'] ? 'font-medium' : 'font-normal'} text-gray-500`}
        >
          A/S Regulation Down
        </span>

        <span className="mr-3 text-gray-500">|</span>

        <span
          onClick={() => toggleVisibility('a_s_reg_up')}
          className={`text-gray-500 ${visibility['a_s_reg_up'] ? 'font-medium' : 'font-normal'}`}
        >
          A/S Regulation Up
        </span>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ top: 40, right: 30, left: -10, bottom: 30 }}>
          <defs>
            <linearGradient id="colorLine1" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8884d8" />
              <stop offset="100%" stopColor="#82ca9d" />
            </linearGradient>
            <linearGradient id="colorLine2" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ff7300" />
              <stop offset="100%" stopColor="#ff6b6b" />
            </linearGradient>
          </defs>

          {/* X Axis - Hide axis line and labels */}
          <XAxis dataKey="name" tick={true} axisLine={true} />

          {/* Y Axis - Hide axis line and labels */}
          <YAxis tick={true} axisLine={true} />

          {/* Tooltip with improved styling */}
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              fontFamily: 'Poppins, sans-serif',
            }}
            itemStyle={{ fontWeight: 'bold' }}
            formatter={(value: any, name: string) => {
              if (name === 'volume1') {
                return <span style={{ color: '#8884d8' }}>{`${value}`}</span>;
              }
              if (name === 'volume2') {
                return <span style={{ color: '#ff6b6b' }}>{`${value}`}</span>;
              }
              return value;
            }}
            labelFormatter={(value: any) => <span style={{ fontWeight: 'bold', color: '#333' }}>{value}</span>}
            cursor={{ stroke: '#ccc', strokeWidth: 1 }}
          />

          {/* Lines */}
          {visibility['a_s_reg_down'] && (
            <Line
              type="stepAfter"
              dataKey="volume1"
              stroke="url(#colorLine1)"
              strokeWidth={3}
              dot={{
                r: 6,
                fill: '#82ca9d',
                stroke: '#8884d8',
                strokeWidth: 2,
              }}
              activeDot={{
                r: 8,
                fill: '#8884d8',
                stroke: '#82ca9d',
                strokeWidth: 2,
              }}
            />
          )}
          {visibility['a_s_reg_up'] && (
            <Line
              type="stepAfter"
              dataKey="volume2"
              stroke="url(#colorLine2)"
              strokeWidth={3}
              dot={{
                r: 6,
                fill: '#ff6b6b',
                stroke: '#ff7300',
                strokeWidth: 2,
              }}
              activeDot={{
                r: 8,
                fill: '#ff7300',
                stroke: '#ff6b6b',
                strokeWidth: 2,
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Legend Section */}
      <div className="flex flex-wrap gap-8 justify-between mt-8 mb-8 sm:flex-row flex-col">
  {/* Ancillary Services Legend */}
  <div className="flex flex-col border rounded-lg p-4 flex-1 bg-[#F6F6F6]">
    <h4 className="text-lg text-gray-600 font-semibold mb-4">Ancillary Services</h4>
    <div className="grid grid-cols-2 gap-4 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400">
            {initialDatasetsConfig
              .filter((dataset) => dataset.label.startsWith('A/S'))
              .map((dataset) => (
                <label key={dataset.id} className="flex items-center gap-2 text-xs sm:text-sm">
                  <input
                    type="checkbox"
                    checked={visibility[dataset.id]}
                    onChange={() => toggleVisibility(dataset.id)}
                    className="w-4 h-4 accent-violet-500"
                  />
                  <span className="flex items-center">
                    <span
                      className="inline-block w-4 h-[2px] mr-2"
                      style={{ backgroundColor: dataset.color }}
                    />
                    {dataset.label}
                  </span>
                </label>
              ))}
          </div>
        </div>

        <div className="flex flex-col border rounded-lg p-4 flex-1 bg-[#F6F6F6]">
    <h4 className="text-lg text-gray-600 font-semibold mb-4">Energy</h4>
    <div className="grid grid-cols-2 gap-4 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400">
            {initialDatasetsConfig
              .filter((dataset) => !dataset.label.startsWith('A/S'))
              .map((dataset) => (
                <label key={dataset.id} className="flex items-center gap-2 text-xs sm:text-sm">
                  <input
                    type="checkbox"
                    checked={visibility[dataset.id]}
                    onChange={() => toggleVisibility(dataset.id)}
                    className="w-4 h-4 accent-violet-500"
                  />
                  <span className="flex items-center">
                    <span
                      className="inline-block w-4 h-[2px] mr-2"
                      style={{ backgroundColor: dataset.color }}
                    />
                    {dataset.label}
                  </span>
                </label>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepChart;
